import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Calendar, Image as ImageIcon, AlertCircle, Video } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { API } from '../utils/api';

const DecouvrirAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    images: [],
    video: '',
    link_url: '',
    link_text: '',
    date: new Date().toISOString().split('T')[0],
    category: 'produit'
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/decouvrir`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching découvrir items:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les éléments Découvrir",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une image valide",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Use the URL returned by backend
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, image: imageUrl }));
      
      console.log('Image uploaded successfully. URL:', imageUrl);
      
      toast({
        title: "Succès",
        description: "Image téléchargée avec succès",
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors du téléchargement de l'image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleFilesSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (files.length > 10) {
      toast({
        title: "Trop d'images",
        description: "Maximum 10 images autorisées",
        variant: "destructive"
      });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Type non autorisé",
          description: `${file.name}: Utilisez JPG, PNG, GIF ou WEBP`,
          variant: "destructive"
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Fichier trop volumineux",
          description: `${file.name}: Maximum 10MB`,
          variant: "destructive"
        });
        return;
      }
    }

    setSelectedFiles(files);
    setUploadingMultiple(true);

    try {
      const uploadFormData = new FormData();
      files.forEach(file => {
        uploadFormData.append('files', file);
      });

      const response = await axios.post(`${API}/upload-multiple`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, images: [...prev.images, ...response.data.urls] }));
      
      toast({
        title: "✅ Succès",
        description: `${response.data.count} image(s) uploadée(s) !`
      });
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      toast({
        title: "❌ Erreur",
        description: "Impossible d'uploader les images",
        variant: "destructive"
      });
    } finally {
      setUploadingMultiple(false);
    }
  };

  const removeGalleryImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Upload vidéo
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Type de fichier non autorisé",
        description: "Utilisez MP4, WebM, MOV, AVI ou MPEG",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximum est de 100MB",
        variant: "destructive"
      });
      return;
    }

    setUploadingVideo(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-video`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, video: response.data.url }));
      toast({ title: "✅ Succès", description: "Vidéo uploadée avec succès !" });
    } catch (error) {
      toast({
        title: "❌ Erreur",
        description: error.response?.data?.detail || "Impossible d'uploader la vidéo",
        variant: "destructive"
      });
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Le titre est requis';
    if (!formData.description.trim()) newErrors.description = 'La description est requise';
    // Image OU vidéo requis
    if (!formData.image && !formData.video) {
      newErrors.media = "Veuillez ajouter au moins une image ou une vidéo";
    }
    if (!formData.date) newErrors.date = 'La date est requise';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs requis",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/decouvrir/${editingItem.id}`, formData);
        toast({
          title: "Succès",
          description: "Élément Découvrir mis à jour avec succès",
        });
      } else {
        await axios.post(`${API}/decouvrir`, formData);
        toast({
          title: "Succès",
          description: "Élément Découvrir créé avec succès",
        });
      }
      
      fetchItems();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving découvrir item:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de l'enregistrement",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/decouvrir/${id}`);
      toast({
        title: "Succès",
        description: "Élément Découvrir supprimé avec succès",
      });
      fetchItems();
    } catch (error) {
      console.error('Error deleting découvrir item:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la suppression",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      image: item.image,
      images: item.images || [],
      video: item.video || '',
      link_url: item.link_url || '',
      link_text: item.link_text || '',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      category: item.category || 'produit'
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      images: [],
      video: '',
      link_url: '',
      link_text: '',
      date: new Date().toISOString().split('T')[0],
      category: 'produit'
    });
    setErrors({});
    setSelectedFile(null);
    setSelectedFiles([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion de "Découvrir"</h2>
          <p className="text-sm text-gray-600 mt-1">Ajoutez et gérez les éléments de la section Découvrir</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un élément
        </button>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Chargement...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucun élément. Commencez par en ajouter un !</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-200 relative">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    {item.date ? new Date(item.date).toLocaleDateString('fr-FR') : 'Date non définie'}
                  </div>
                  <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded">
                    {item.category || 'produit'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setViewingItem(item);
                      setShowViewModal(true);
                    }}
                    className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Voir les détails"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-600 px-3 py-2 rounded hover:bg-blue-100 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="flex-1 flex items-center justify-center gap-2 bg-red-50 text-red-600 px-3 py-2 rounded hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Modifier l\'élément' : 'Nouvel élément Découvrir'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Titre de l'élément"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Description de l'élément"
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>

              {/* Lien cliquable */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">🔗 Lien cliquable (optionnel)</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">URL du lien</label>
                  <input
                    type="url"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mot à rendre cliquable</label>
                  <input
                    type="text"
                    value={formData.link_text}
                    onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: focaccia"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Le mot sera affiché en <span className="text-blue-600 underline">bleu et souligné</span>
                  </p>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="produit">Produit</option>
                  <option value="recette">Recette</option>
                  <option value="conseil">Conseil</option>
                  <option value="tradition">Tradition</option>
                </select>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image <span className="text-gray-400 text-xs">(optionnel si vidéo)</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {uploading && <p className="text-sm text-blue-600">Téléchargement en cours...</p>}
                  {formData.image && (
                    <div className="mt-2">
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="h-32 w-auto rounded-lg border border-gray-300"
                      />
                    </div>
                  )}
                </div>
                {errors.media && !formData.image && !formData.video && (
                  <p className="text-red-500 text-sm mt-1">{errors.media}</p>
                )}
              </div>

              {/* Galerie d'images */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-purple-900 mb-3">
                  🖼️ Galerie d'images (optionnel - max 10)
                </label>
                <p className="text-xs text-gray-600 mb-3">Ajoutez plusieurs images pour créer un carrousel</p>
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMultipleFilesSelect}
                  disabled={uploadingMultiple}
                  multiple
                  className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                
                {uploadingMultiple && <p className="text-sm text-purple-600 mt-2">⏳ Upload en cours...</p>}
                
                {formData.images.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {formData.images.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Galerie ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-purple-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <p className="text-xs text-center text-gray-600 mt-1">{index + 1}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Vidéo (optionnel) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Video className="w-4 h-4 inline mr-2" />
                  Vidéo (optionnel)
                </label>
                
                {formData.video ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <video 
                        src={formData.video} 
                        controls 
                        className="w-full h-48 object-cover rounded-lg bg-black"
                      />
                      <button
                        type="button"
                        onClick={removeVideo}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-green-600">✓ Vidéo ajoutée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors ${uploadingVideo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
                        onChange={handleVideoUpload}
                        className="hidden"
                        disabled={uploadingVideo}
                      />
                      <Video className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {uploadingVideo ? 'Upload en cours...' : 'Choisir une vidéo (max 100MB)'}
                      </span>
                    </label>
                    <div className="text-center text-gray-400 text-sm">ou</div>
                    <input
                      type="url"
                      value={formData.video}
                      onChange={(e) => setFormData({ ...formData, video: e.target.value })}
                      placeholder="Coller l'URL d'une vidéo YouTube ou autre"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500">Formats: MP4, WebM, MOV, AVI, MPEG. Ou collez un lien YouTube.</p>
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {editingItem ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de visualisation plein écran */}
      {showViewModal && viewingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Détails de l'élément Découvrir</h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingItem(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Image en grand */}
              {viewingItem.image && (
                <div className="w-full rounded-lg overflow-hidden">
                  <img
                    src={viewingItem.image}
                    alt={viewingItem.title}
                    className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                  />
                </div>
              )}

              {/* Titre */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">TITRE</h4>
                <p className="text-2xl font-bold text-gray-900">{viewingItem.title}</p>
              </div>

              {/* Description complète */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">DESCRIPTION</h4>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {viewingItem.description}
                </p>
              </div>

              {/* Informations supplémentaires */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">DATE</h4>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Calendar className="w-5 h-5" />
                    <span className="text-lg">
                      {viewingItem.date ? new Date(viewingItem.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Date non définie'}
                    </span>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">CATÉGORIE</h4>
                  <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                    {viewingItem.category || 'produit'}
                  </span>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingItem);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Pencil className="w-5 h-5" />
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingItem(null);
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DecouvrirAdmin;
